import { UserManagementFeatureFlagKeyType, UserManagementFeatureFlagsType, UserManagementUserType } from './types';
import { UserManagementCommunityOption } from './UserManagementSelectedCommunities';

export const getInitialSelectedCommunities = (user?: UserManagementUserType): UserManagementCommunityOption[] =>
  (user?.communities || []).map((community) => ({
    id: community.id,
    comunity_name: community.comunity_name,
    address: community.address,
    city: community.city,
    state: community.state,
    zip_code: community.zip_code,
    company: user?.company ? { id: user.company.id, name: user.company.name } : null,
  }));

type UserManagementEditableValues = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  position_id?: number | null;
  is_active?: boolean | null;
  company_id?: number | null;
  holding_id?: number | null;
  communities_ids?: number[];
};

const normalizeString = (value?: string | null) => (value || '').trim();

const normalizeCommunities = (communitiesIds?: number[]) => [...(communitiesIds || [])].sort((a, b) => a - b);

export const getNormalizedEditableUserState = (values: UserManagementEditableValues) => ({
  first_name: normalizeString(values.first_name),
  last_name: normalizeString(values.last_name),
  email: normalizeString(values.email).toLowerCase(),
  position_id: values.position_id ?? null,
  is_active: Boolean(values.is_active),
  company_id: values.company_id ?? null,
  holding_id: values.holding_id ?? null,
  communities_ids: normalizeCommunities(values.communities_ids),
});

export const getInitialEditableUserState = (user?: UserManagementUserType) =>
  getNormalizedEditableUserState({
    first_name: user?.first_name,
    last_name: user?.last_name,
    email: user?.email,
    position_id: user?.position?.id,
    is_active: user?.is_active,
    company_id: user?.company?.id,
    holding_id: user?.holding?.id,
    communities_ids: (user?.communities || []).map((community) => community.id),
  });

export const hasUserManagementFeatureFlagChanges = (
  isBulkMode: boolean,
  bulkFeatureFlagChanges: Partial<UserManagementFeatureFlagsType>,
  featureFlags: UserManagementFeatureFlagsType | null,
  initialFeatureFlags: UserManagementFeatureFlagsType | null
) => {
  if (isBulkMode) {
    return Object.keys(bulkFeatureFlagChanges).length > 0;
  }

  if (!featureFlags || !initialFeatureFlags) {
    return false;
  }

  return JSON.stringify(featureFlags) !== JSON.stringify(initialFeatureFlags);
};

export const getUpdatedBulkFeatureFlagChanges = (
  bulkFeatureFlagChanges: Partial<UserManagementFeatureFlagsType>,
  key: UserManagementFeatureFlagKeyType,
  value: boolean
) => {
  if (bulkFeatureFlagChanges[key] !== value) {
    return bulkFeatureFlagChanges;
  }

  const nextBulkFeatureFlagChanges = { ...bulkFeatureFlagChanges };
  delete nextBulkFeatureFlagChanges[key];
  return nextBulkFeatureFlagChanges;
};

export const getCurrentFeatureFlagAccessText = (enabledCount: number, usersCount: number) => {
  if (enabledCount === 0) {
    return 'None currently have access';
  }

  if (enabledCount === usersCount) {
    return 'All currently have access';
  }

  return `${enabledCount} of ${usersCount} currently have access`;
};

export const getBulkFeatureFlagCountText = (enabledCount: number, usersCount: number, change: boolean | undefined) => {
  const currentAccessText = getCurrentFeatureFlagAccessText(enabledCount, usersCount);

  if (change === true) {
    return `${currentAccessText} -- will activate for all ${usersCount}`;
  }

  if (change === false) {
    return `${currentAccessText} -- will deactivate for all ${usersCount}`;
  }

  return currentAccessText;
};

export const hasSingleFeatureFlagChanged = (
  key: UserManagementFeatureFlagKeyType,
  featureFlags: UserManagementFeatureFlagsType | null,
  initialFeatureFlags: UserManagementFeatureFlagsType | null
) => Boolean(featureFlags && initialFeatureFlags && featureFlags[key] !== initialFeatureFlags[key]);
